import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { subscriptionApi } from '../services/subscription.service';
import { SubscriptionProrationPreview } from '../types/subscription';

type PaymentMode = 'new' | 'upgrade';

interface PaymentState {
  planId: string;
  planName: string;
  amount: number;
  currency?: string;
  billingCycle?: 'monthly' | 'annual';
  mode?: PaymentMode;
  originalAmount?: number;
  prorationPreview?: SubscriptionProrationPreview;
}

interface PendingSubscriptionPurchase extends PaymentState {
  role: 'TEACHER' | 'STUDENT';
}

const PENDING_SUBSCRIPTION_KEY = 'pendingSubscriptionPurchase';

const SubscriptionPaymentPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const currentUser: any = (user as any)?.user ? (user as any).user : user;
  const role = currentUser?.role === 'TEACHER' ? 'TEACHER' : 'STUDENT';
  const state = (location.state as PaymentState | null) || null;
  const [loading, setLoading] = useState(false);
  const [prorationPreview, setProrationPreview] = useState<SubscriptionProrationPreview | null>(
    state?.prorationPreview || null,
  );
  const [previewLoading, setPreviewLoading] = useState(false);

  const paymentState = useMemo(() => {
    if (!state?.planId || !state?.planName) return null;
    return {
      planId: state.planId,
      planName: state.planName,
      amount: Number(prorationPreview?.amountDue ?? state.amount ?? 0),
      originalAmount: Number(state.originalAmount ?? state.amount ?? 0),
      currency: state.currency || 'INR',
      billingCycle: state.billingCycle || 'monthly',
      mode: state.mode || 'new',
      prorationPreview: prorationPreview || undefined,
    };
  }, [prorationPreview, state]);

  useEffect(() => {
    if (!state?.planId || state.mode !== 'upgrade' || state.prorationPreview) return;
    let cancelled = false;

    const loadPreview = async () => {
      try {
        setPreviewLoading(true);
        const preview = await subscriptionApi.previewPlanSwitch(state.planId);
        if (!cancelled) {
          setProrationPreview(preview);
        }
      } catch (error) {
        console.error('Failed to calculate prorated subscription amount:', error);
      } finally {
        if (!cancelled) {
          setPreviewLoading(false);
        }
      }
    };

    void loadPreview();
    return () => {
      cancelled = true;
    };
  }, [state?.planId, state?.mode, state?.prorationPreview]);

  const subscribeWithWallet = async () => {
    if (!paymentState) return;

    try {
      setLoading(true);
      if (role === 'TEACHER') {
        await api.post(`/teachers/subscription/subscribe/${paymentState.planId}`, {
          paymentMethodId: 'WALLET',
        });
      } else if (paymentState.mode === 'upgrade') {
        await subscriptionApi.upgradeSubscription({ newPlanId: paymentState.planId, prorate: true });
      } else {
        await subscriptionApi.createSubscription({
          planId: paymentState.planId,
          billingCycle: paymentState.billingCycle || 'monthly',
        });
      }

      alert('Subscription payment successful.');
      navigate(role === 'TEACHER' ? '/teacher/subscription-plans' : '/subscriptions');
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Subscription payment failed.';
      alert(message);
    } finally {
      setLoading(false);
    }
  };

  const continueWithRazorpay = () => {
    if (!paymentState) return;

    const pendingPurchase: PendingSubscriptionPurchase = {
      ...paymentState,
      role,
    };
    localStorage.setItem(PENDING_SUBSCRIPTION_KEY, JSON.stringify(pendingPurchase));
    navigate('/student/wallet/topup', { state: { amount: paymentState.amount } });
  };

  if (!paymentState) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-md p-4">
          Plan details are missing. Please choose a plan again.
        </div>
        <button
          type="button"
          onClick={() => navigate(role === 'TEACHER' ? '/teacher/subscription-plans' : '/subscriptions')}
          className="mt-4 inline-flex items-center px-4 py-2 rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
        >
          Back To Plans
        </button>
      </div>
    );
  }

  const symbol = paymentState.currency === 'INR' ? 'Rs.' : `${paymentState.currency || ''} `;

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900">Subscription Payment</h1>
        <p className="mt-2 text-gray-600">{paymentState.planName}</p>
        <p className="mt-1 text-xl font-semibold text-gray-900">
          {symbol}
          {paymentState.amount.toFixed(2)} / {paymentState.billingCycle}
        </p>
        {previewLoading ? (
          <p className="mt-2 text-sm text-gray-500">Calculating prorated switch amount...</p>
        ) : paymentState.prorationPreview ? (
          <p className="mt-2 text-sm text-gray-600">
            Reduced from {symbol}{Number(paymentState.originalAmount || 0).toFixed(2)} with {symbol}{Number(paymentState.prorationPreview.currentPlanUnusedCredit || 0).toFixed(2)} credit for {paymentState.prorationPreview.remainingDays} remaining days.
          </p>
        ) : null}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Pay From Wallet</h2>
          <p className="text-sm text-gray-600 mt-2">
            Instantly complete subscription using your wallet balance.
          </p>
          <button
            type="button"
            disabled={loading || previewLoading}
            onClick={subscribeWithWallet}
            className="mt-4 rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
          >
            {loading ? 'Processing...' : 'Pay With Wallet'}
          </button>
        </div>

        <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">UPI / Razorpay</h2>
          <p className="text-sm text-gray-600 mt-2">
            Add amount via Razorpay and complete subscription from wallet.
          </p>
          <button
            type="button"
            disabled={loading || previewLoading || paymentState.amount <= 0}
            onClick={continueWithRazorpay}
            className="mt-4 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
          >
            Continue To Razorpay
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPaymentPage;
